import {PlusOutlined, UploadOutlined} from '@ant-design/icons';
import {Button, Col, Form, Input, message, Modal, Row, Upload} from 'antd';
import {useState} from 'react';
import {capitalize} from 'lodash';
import {useTranslation} from 'react-i18next';
import {
  useFileUploadToS3Mutation,
  usePatchDocumentUploadStatusMutation,
  useRequestFileUploadToS3Mutation,
} from '../../../../redux/api/documents';
import {
  ACCEPTED_FILE_EXTENSIONS,
  ACCEPTED_IMAGES_CONTENT_TYPE,
  MAX_FILE_SIZE,
  sanitize_document_name,
  SPECIAL_CHARS_TO_ESCAPE,
} from '../../../../utils/documents';

const {TextArea} = Input;
const {Dragger} = Upload;
export const UploadImageModal = ({onAfterUpload}: { onAfterUpload?: (uploadedDocumentUuid: string) => void }) => {

  const {t} = useTranslation();

  const [addImageForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const [
    isModalOpen,
    setIsModalOpen,
  ] = useState(false);
  const [
    fileIsOk,
    setFileIsOk,
  ] = useState(true);
  const [
    isUploadingFile,
    setIsUploadingFile,
  ] = useState(false);

  const [
    requestAWSUrl,
  ] = useRequestFileUploadToS3Mutation();

  const [
    fileUpload,
  ] = useFileUploadToS3Mutation();

  const [
    patchImageUploadStatus,
  ] = usePatchDocumentUploadStatusMutation();

  const handleFinish = async (values: any) => {
    setIsUploadingFile(true);
    try {
      const awsUrl = await requestAWSUrl({
        name: sanitize_document_name(values.document_name),
        start_date: null,
        end_date: null,
        description: values.description || '',
        content_type: values.document.file.type,
        type: 'P',
        file_extension: values.document.file.name.slice((values.document.file.name.lastIndexOf('.') - 1 >>> 0) + 2) || 'jpg',
      }).unwrap();
      const postData = new FormData();
      for (const [key, value] of Object.entries(awsUrl.fields)) {
        postData.append(key, value);
      }
      postData.append('file', addImageForm.getFieldValue('document').file);
      await fileUpload({url: awsUrl.url, postData: postData}).unwrap();
      await patchImageUploadStatus({documentUuid: awsUrl.uuid, is_valid: true});
      if (onAfterUpload) {
        await onAfterUpload(awsUrl.uuid);
      }
      messageApi.success(capitalize(t('form.creationSuccessMessage')));
    } catch (e) {
      messageApi.error(capitalize(t('form.creationFailMessage')));
    } finally {
      setIsUploadingFile(false);
      setIsModalOpen(false);
      addImageForm.resetFields();
    }
  };

  return (
    <>
      {contextHolder}
      <Modal
        open={isModalOpen}
        title={capitalize(t('actions.uploadItem', {item: t('documents.image', {count: 1})}))}
        onCancel={() => {
          setIsModalOpen(false);
          addImageForm.resetFields();
        }}
        centered={true}
        destroyOnClose={true}
        footer={null}
      >
        <Form
          form={addImageForm}
          layout={'vertical'}
          onFinish={handleFinish}
          onFinishFailed={errorInfo => console.log('failed: ', errorInfo)}
          autoComplete={'off'}
        >
          <Form.Item
            name={'document_name'}
            label={capitalize(t('documents.imageName', {count: 1}))}
            dependencies={['document']}
            rules={[
              {
                required: true,
                message: capitalize(t('documents.imageNameRequired')),
                validator: (_, value) => {
                  if (!value || value.trim() === '') {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
              },
              ({getFieldValue}) => ({
                validator(_, value) {
                  if (value && value.trim() && SPECIAL_CHARS_TO_ESCAPE.some(char => value.includes(char))) {
                    return Promise.reject(new Error(capitalize(t('documents.noSpecialCharsAllowed'))));
                  }
                  return Promise.resolve();
                },
              }),
              {
                warningOnly: true,
                validator: (_, value) => {
                  if (value && value.trim() && ACCEPTED_FILE_EXTENSIONS.some(ext => value.includes(ext))) {
                    return Promise.reject(new Error(capitalize(t('documents.fileExtensionIsOptional'))));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder={capitalize(t('documents.imageName', {count: 1}))}/>
          </Form.Item>
          <Form.Item
            name={'description'}
            label={capitalize(t('documents.imageDescription'))}
          >
            <TextArea
              rows={2}
              placeholder={capitalize(t('documents.imageDescription'))}
            />
          </Form.Item>
          <Form.Item
            name={'document'}
            rules={[{required: true, message: capitalize(t('documents.imageRequired'))}]}
            label={capitalize(t('documents.fileUpload'))}
          >
            <Dragger
              name={'file'}
              multiple={false}
              maxCount={1}
              accept={ACCEPTED_IMAGES_CONTENT_TYPE.join(',')}
              beforeUpload={(file) => {
                if (file.size <= MAX_FILE_SIZE) {
                  setFileIsOk(true);
                } else {
                  setFileIsOk(false);
                }
                if (!addImageForm.getFieldValue('document_name')) {
                  addImageForm.setFieldValue('document_name', file.name);
                }
                return false;
              }}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined/>
              </p>
              <p className="ant-upload-text">
                {capitalize(t('documents.dragOrSelectImage'))}
              </p>
            </Dragger>
          </Form.Item>
          <Row
            justify={'end'}
            gutter={[24, 24]}
          >
            <Col>
              <Form.Item>
                <Button
                  type={'default'}
                  onClick={() => {
                    setIsModalOpen(false);
                    addImageForm.resetFields();
                  }}
                >
                  {capitalize(t('form.cancel'))}
                </Button>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item>
                <Button
                  type={'primary'}
                  loading={isUploadingFile}
                  disabled={!fileIsOk}
                  htmlType={'submit'}
                >
                  {capitalize(t('actions.uploadItem', {item: t('documents.image', {count: 1})}))}
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Button
        type={'primary'}
        icon={<PlusOutlined/>}
        onClick={() => setIsModalOpen(true)}
      >
        {capitalize(t('actions.uploadItem', {item: t('documents.image', {count: 1})}))}
      </Button>
    </>
  );
};